import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import "./Styles/FormList.css";
import {
    faUndo, faEye, faSearch, faRefresh, faSpinner, faExclamationTriangle,
    faCheckCircle, faTimes, faChevronLeft, faChevronRight, faFilter,faChevronDown,
    faCalendarAlt, faFilePdf, faPrint, faDownload, faCalendar,
    faBox, faTag, faUser, faPhone, faDollarSign, faCalculator,
    faExclamationCircle, faHashtag, faShoppingCart, faStore,
    faReceipt, faHistory, faMoneyBillWave, faExchangeAlt, faEyeSlash,
    faInfoCircle, faBoxes, faBalanceScale, faEdit, faSave, faCreditCard, faTrash, faPlus
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper function to adjust color brightness
const adjustColor = (color, amount) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) return '#6366f1';
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Custom Dropdown Component
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    // Sync search term with selected value ONLY when not hovering
    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    // Close all other dropdowns when this one opens
    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: label } });
            window.dispatchEvent(event);
        }
    }, [isOpen, label]);

    // Listen for dropdown opened events from other dropdowns
    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== label) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [label]);

    // Filter options - SHOW ALL when hovering, otherwise filter by search term
    const filteredOptions = isHovering
        ? options // Show ALL options when hovering
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    // Hover to open - shows ALL options
    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        // Don't close immediately - let click outside handle it
        // But reset hovering state after a delay
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    // Handle input click
    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    // Handle input change (typing)
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div 
            className="ozlistui-custom-dropdown-wrapper" 
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ozlistui-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Helper function to escape HTML
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Create translator instance
const createReturnsListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Returns List': 'واپسی کی فہرست',
                'All return details are below': 'تمام واپسی کی تفصیلات نیچے ہیں',
                'Return Number': 'واپسی نمبر',
                'Customer Name': 'کسٹمر کا نام',
                'Phone': 'فون نمبر',
                'Date': 'تاریخ',
                'Grand Total': 'کل رقم',
                'Refund Amount': 'واپسی رقم',
                'Items': 'آئٹمز',
                'Return Reason': 'واپسی کی وجہ',
                'Actions': 'اعمال',
                'Added By': 'شامل کرنے والا',
                'Search returns...': 'واپسی تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'View': 'دیکھیں',
                'Close': 'بند کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Showing': 'دکھا رہا ہے',
                'of': 'میں سے',
                'Previous': 'پچھلا',
                'Next': 'اگلا',
                'Switch Shop': 'شاپ تبدیل کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view returns': 'براہ کرم واپسی دیکھنے کے لیے شاپ منتخب کریں',
                'S.No': 'نمبر',
                'Returns': 'واپسی',
                'Shop': 'شاپ',
                'Return Details': 'واپسی کی تفصیلات',
                'Total Returns': 'کل واپسی',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'FILTERS APPLIED': 'فلٹرز لاگو ہیں',
                'Search': 'تلاش کریں',
                'From': 'سے',
                'To': 'تک',
                'Page': 'صفحہ',
                'All': 'سب',
                'Date Range': 'تاریخ کی حد',
                'Start Date': 'شروع تاریخ',
                'End Date': 'اختتام تاریخ',
                'Orientation': 'اورینٹیشن',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'A4': 'A4',
                'Letter': 'لیٹر',
                'Legal': 'لیگل',
                'A5': 'A5',
                'Returns Report': 'واپسی رپورٹ',
                'Total Refund': 'کل واپسی رقم',
                'Clear': 'صاف کریں',
                'Hide Filters': 'فلٹرز چھپائیں',
                'Filters': 'فلٹرز'
            },
            'ar': {
                'Returns List': 'قائمة الإرجاعات',
                'All return details are below': 'جميع تفاصيل الإرجاعات أدناه',
                'Return Number': 'رقم الإرجاع',
                'Customer Name': 'اسم العميل',
                'Phone': 'رقم الهاتف',
                'Date': 'التاريخ',
                'Grand Total': 'المجموع الكلي',
                'Refund Amount': 'مبلغ الاسترداد',
                'Items': 'العناصر',
                'Return Reason': 'سبب الإرجاع',
                'Actions': 'الإجراءات',
                'Added By': 'أضيف بواسطة',
                'Search returns...': 'بحث عن إرجاعات...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'View': 'عرض',
                'Close': 'إغلاق',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Showing': 'عرض',
                'of': 'من',
                'Previous': 'السابق',
                'Next': 'التالي',
                'Switch Shop': 'تبديل المتجر',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view returns': 'الرجاء اختيار متجر لعرض الإرجاعات',
                'S.No': 'رقم',
                'Returns': 'إرجاعات',
                'Shop': 'متجر',
                'Return Details': 'تفاصيل الإرجاع',
                'Total Returns': 'إجمالي الإرجاعات',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'FILTERS APPLIED': 'المرشحات المطبقة',
                'Search': 'بحث',
                'From': 'من',
                'To': 'إلى',
                'Page': 'صفحة',
                'All': 'الكل',
                'Date Range': 'نطاق التاريخ',
                'Start Date': 'تاريخ البدء',
                'End Date': 'تاريخ الانتهاء',
                'Orientation': 'الاتجاه',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'A4': 'A4',
                'Letter': 'رسالة',
                'Legal': 'قانوني',
                'A5': 'A5',
                'Returns Report': 'تقرير الإرجاعات',
                'Total Refund': 'إجمالي الاسترداد',
                'Clear': 'مسح',
                'Hide Filters': 'إخفاء المرشحات',
                'Filters': 'مرشحات'
            },
            'es': {
                'Returns List': 'Lista de Devoluciones',
                'All return details are below': 'Todos los detalles de devolución están abajo',
                'Return Number': 'Número de Devolución',
                'Customer Name': 'Nombre del Cliente',
                'Phone': 'Teléfono',
                'Date': 'Fecha',
                'Grand Total': 'Total General',
                'Refund Amount': 'Monto de Reembolso',
                'Items': 'Elementos',
                'Return Reason': 'Motivo de Devolución',
                'Actions': 'Acciones',
                'Added By': 'Agregado Por',
                'Search returns...': 'Buscar devoluciones...',
                'Refresh': 'Actualizar',
                'Export PDF': 'Exportar PDF',
                'View': 'Ver',
                'Close': 'Cerrar',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Showing': 'Mostrando',
                'of': 'de',
                'Previous': 'Anterior',
                'Next': 'Siguiente',
                'Switch Shop': 'Cambiar Tienda',
                'No shop selected': 'No se seleccionó ninguna tienda',
                'Please select a shop to view returns': 'Por favor seleccione una tienda para ver las devoluciones',
                'S.No': 'N°',
                'Returns': 'Devoluciones',
                'Shop': 'Tienda',
                'Return Details': 'Detalles de Devolución',
                'Total Returns': 'Total de Devoluciones',
                'Generated': 'Generado',
                'Report ID': 'ID del Reporte',
                'Generated by': 'Generado por',
                'Shop Code': 'Código de Tienda',
                'FILTERS APPLIED': 'FILTROS APLICADOS',
                'Search': 'Buscar',
                'From': 'Desde',
                'To': 'Hasta',
                'Page': 'Página',
                'All': 'Todos',
                'Date Range': 'Rango de Fechas',
                'Start Date': 'Fecha Inicio',
                'End Date': 'Fecha Fin',
                'Orientation': 'Orientación',
                'Portrait': 'Vertical',
                'Landscape': 'Horizontal',
                'A4': 'A4',
                'Letter': 'Carta',
                'Legal': 'Legal',
                'A5': 'A5',
                'Returns Report': 'Reporte de Devoluciones',
                'Total Refund': 'Reembolso Total',
                'Clear': 'Limpiar',
                'Hide Filters': 'Ocultar Filtros',
                'Filters': 'Filtros'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) { }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        returnsList: 'Returns List',
        formDetails: 'All return details are below',
        returns: 'Returns',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search returns...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your return data...',
        selectShop: 'Please select a shop to view returns',
        returnNumber: 'Return Number',
        customerName: 'Customer Name',
        phone: 'Phone',
        date: 'Date',
        grandTotal: 'Grand Total',
        refundAmount: 'Refund Amount',
        items: 'Items',
        returnReason: 'Return Reason',
        actions: 'Actions',
        addedBy: 'Added By',
        view: 'View',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        returnDetails: 'Return Details',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        returnsReport: 'Returns Report',
        totalReturns: 'Total Returns',
        totalRefund: 'Total Refund',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        clear: 'Clear',
        hideFilters: 'Hide Filters',
        filters: 'Filters'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const returnsListTranslator = createReturnsListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};


// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading, darkMode }) => {
    if (!isOpen) return null;

    return (
        <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main Component
const SolarReturnList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [returns, setReturns] = useState([]);
    const [filteredReturns, setFilteredReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editDetailsLoading, setEditDetailsLoading] = useState(false);
    const [editFormData, setEditFormData] = useState({
        id: '', date: '', customer_name: '', customer_phone: '', payment_method: 'cash',
        return_reason: '', notes: '', subtotal: '0', tax: '0', discount: '0',
        grand_total: '0', refund_amount: '0', items: []
    });
    const [editSaleItems, setEditSaleItems] = useState([]);
    const [editItemToAdd, setEditItemToAdd] = useState('');
    const [editItemErrors, setEditItemErrors] = useState({});
    const [showFilters, setShowFilters] = useState(false);

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const editDetailsRequestRef = useRef(0);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        bussinesslogo_url: '',
        businessaddress: '',
        fullname: '',
        email: '',
        phonenumber: '',
        gst_number: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    const pdfPageRangeOptions = useMemo(() => [
        { value: 'all', label: 'All Pages' },
        { value: 'range', label: 'Page Range' }
    ], []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============================================
    // THEME SYNC
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozlistui-primary', color);
        document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
        document.documentElement.style.setProperty('--ozlistui-primary-light', color);
        setCurrentTheme(color);
        
        let style = document.getElementById('dynamic-list-styles-returns');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-list-styles-returns';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozlistui-table thead th {
                color: ${color};
                border-bottom-color: ${color};
            }
            .ozlistui-payment-badge {
                border-color: ${color}40;
            }
            .ozlistui-page-number.ozlistui-active {
                background: ${color};
                border-color: ${color};
            }
            .ozlistui-header-shop-btn,
            .ozlistui-header-filter-btn {
                background: linear-gradient(135deg, ${color}, ${adjustColor(color, -20)});
            }
            .ozlistui-btn-primary {
                background: ${color};
            }
        `;
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentTheme]);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    const syncDarkMode = useCallback(() => {
        const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
        setDarkMode(isDark);

        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isDark) {
                container.classList.add('ozlistui-dark');
            } else {
                container.classList.remove('ozlistui-dark');
            }
        }

        const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
        overlays.forEach(overlay => {
            if (isDark) {
                overlay.classList.add('ozlistui-dark');
            } else {
                overlay.classList.remove('ozlistui-dark');
            }
        });

        if (isDark) {
            document.body.classList.add('ozlistui-dark');
            document.documentElement.classList.add('ozlistui-dark');
        } else {
            document.body.classList.remove('ozlistui-dark');
            document.documentElement.classList.remove('ozlistui-dark');
        }
    }, []);

    useEffect(() => {
        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
                overlays.forEach(overlay => {
                    if (isDark) {
                        overlay.classList.add('ozlistui-dark');
                    } else {
                        overlay.classList.remove('ozlistui-dark');
                    }
                });
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [syncDarkMode, darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    const loadTranslations = useCallback(async (lang) => {
        const trans = await returnsListTranslator.getTranslations(lang);
        setTranslations(trans);
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleStorageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage, loadTranslations]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozlistui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Fetch returns
    const fetchReturns = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setReturns(data.data || []);
                    setFilteredReturns(data.data || []);
                    resetFilters();
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
            if (isMountedRef.current) showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, translations, showToast]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include'
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo_url || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || '',
                        phonenumber: data.phonenumber || '',
                        gst_number: data.gst_number || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || currentShop?.shop_name || '',
                        bussinesslogo: user.bussinesslogo || '',
                        bussinesslogo_url: user.bussinesslogo_url || '',
                        businessaddress: user.business_address || '',
                        fullname: user.fullname || '',
                        email: user.email || '',
                        phonenumber: user.phonenumber || '',
                        gst_number: user.gst_number || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || currentShop?.shop_name || '',
                    bussinesslogo: user.bussinesslogo || '',
                    bussinesslogo_url: user.bussinesslogo_url || '',
                    businessaddress: user.business_address || '',
                    fullname: user.fullname || '',
                    email: user.email || '',
                    phonenumber: user.phonenumber || '',
                    gst_number: user.gst_number || ''
                });
            }
        }
    }, [user, currentShop]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchReturns();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
                fetchReturns();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, fetchReturns]);

    const applyFilters = useCallback(() => {
        let filtered = [...returns];
        
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(ret =>
                ret.return_number?.toLowerCase().includes(term) ||
                ret.customer_name?.toLowerCase().includes(term) ||
                ret.customer_phone?.toLowerCase().includes(term) ||
                ret.username?.toLowerCase().includes(term)
            );
        }
        
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(ret => {
                if (!ret.date) return false;
                const retDate = new Date(ret.date);
                retDate.setHours(0, 0, 0, 0);
                return retDate >= start;
            });
        }
        
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(ret => {
                if (!ret.date) return false;
                const retDate = new Date(ret.date);
                return retDate <= end;
            });
        }
        
        setFilteredReturns(filtered);
        setCurrentPage(1);
    }, [returns, searchTerm, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    const resetFilters = useCallback(() => {
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0';
        return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
    };

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    // Get rows per page
    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') {
            return 20;
        }
        return 15;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredReturns.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentReturns = filteredReturns.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentReturns };
    }, [filteredReturns, currentPage, itemsPerPage]);

    // Calculate totals
    const totalReturns = filteredReturns.length;
    const totalRefund = filteredReturns.reduce((sum, ret) => sum + (parseFloat(ret.refund_amount) || 0), 0);
    const totalAmount = filteredReturns.reduce((sum, ret) => sum + (parseFloat(ret.grand_total) || 0), 0);

    // PDF EXPORT FUNCTION
    const exportToPDF = useCallback(async () => {
        if (!filteredReturns || filteredReturns.length === 0) {
            showToast('warning', translations.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = currentLang === 'ur' || currentLang === 'ar';
            
            const pdfTextColor = '#1f2937';
            const pdfSubTextColor = '#6b7280';
            const pdfBorderColor = '#e5e7eb';
            const pdfHeaderBgColor = '#f9fafb';
            const pdfRowEvenBg = '#ffffff';
            const pdfRowOddBg = '#f9fafb';
            const pdfThemeColor = currentTheme;
            
            const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: pageFormat, compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const rowsPerPage = getRowsPerPage();
            
            let returnsToExport = [...filteredReturns];
            
            if (pdfPageRange === 'range') {
                const totalPagesForRange = Math.ceil(returnsToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesForRange, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, returnsToExport.length);
                returnsToExport = returnsToExport.slice(startIdx, endIdx);
            }
            
            const totalPages = Math.ceil(returnsToExport.length / rowsPerPage);

            const getPDFTranslation = (key, fallback = '') => {
                return translations[key] || fallback;
            };

            const formatCurrencyForPDF = (amount) => {
                if (!amount && amount !== 0) return 'Rs. 0';
                return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
            };

            const formatDateForPDF = (dateString) => {
                if (!dateString) return '-';
                try {
                    const date = new Date(dateString);
                    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                } catch { return dateString; }
            };

            const renderPage = async (pageReturns, pageNum, startRowIndex) => {
                return new Promise((resolve, reject) => {
                    try {
                        const container = document.createElement('div');
                        container.style.width = '750px';
                        container.style.padding = '12px 15px';
                        container.style.backgroundColor = '#ffffff';
                        container.style.color = pdfTextColor;
                        container.style.position = 'absolute';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
                        container.style.direction = isRTL ? 'rtl' : 'ltr';

                        const returnsReportLabel = getPDFTranslation('returnsReport', 'Returns Report');
                        const returnsDirectoryLabel = getPDFTranslation('returns', 'Returns');
                        const totalReturnsLabel = getPDFTranslation('totalReturns', 'Total Returns');
                        const generatedLabel = getPDFTranslation('generated', 'Generated');
                        const reportIdLabel = getPDFTranslation('reportId', 'Report ID');
                        const generatedByLabel = getPDFTranslation('generatedBy', 'Generated by');
                        const shopCodeLabel = getPDFTranslation('shopCode', 'Shop Code');
                        const filtersAppliedLabel = getPDFTranslation('filtersApplied', 'FILTERS APPLIED');
                        const searchLabel = getPDFTranslation('search', 'Search');
                        const fromLabel = getPDFTranslation('from', 'From');
                        const toLabel = getPDFTranslation('to', 'To');
                        const pageLabel = getPDFTranslation('page', 'Page');
                        const ofLabel = getPDFTranslation('of', 'of');
                        const returnNumberLabel = getPDFTranslation('returnNumber', 'Return #');
                        const customerNameLabel = getPDFTranslation('customerName', 'Customer');
                        const phoneLabel = getPDFTranslation('phone', 'Phone');
                        const dateLabel = getPDFTranslation('date', 'Date');
                        const grandTotalLabel = getPDFTranslation('grandTotal', 'Total');
                        const refundAmountLabel = getPDFTranslation('refundAmount', 'Refund');
                        const returnReasonLabel = getPDFTranslation('returnReason', 'Reason');
                        const addedByLabel = getPDFTranslation('addedBy', 'Added By');
                        const itemsLabel = getPDFTranslation('items', 'Items');

                        const totalGrandTotal = pageReturns.reduce((sum, ret) => sum + (parseFloat(ret.grand_total) || 0), 0);
                        const totalRefundAmount = pageReturns.reduce((sum, ret) => sum + (parseFloat(ret.refund_amount) || 0), 0);

                        const headerHTML = `
                            <div style="margin-bottom: 12px; text-align: center;">
                                <div style="font-size: 8px; letter-spacing: 3px; color: ${pdfSubTextColor}; margin-bottom: 4px; text-transform: uppercase; font-weight: 500;">${returnsReportLabel}</div>
                                <h1 style="color: ${pdfTextColor}; font-size: 20px; margin: 0 0 4px 0; font-weight: 600;">
                                    ${escapeHtml(currentShop?.shop_name) || escapeHtml(returnsDirectoryLabel)}
                                </h1>
                                <div style="width: 40px; height: 2px; background: ${pdfThemeColor}; margin: 6px auto;"></div>
                                ${businessInfo.businessname ? `<div style="color: ${pdfSubTextColor}; font-size: 10px; margin-top: 4px;">${escapeHtml(businessInfo.businessname)}</div>` : ''}
                                <div style="color: ${pdfSubTextColor}; font-size: 7px; margin-top: 4px;">
                                    ${totalReturnsLabel}: ${returnsToExport.length} | ${generatedLabel}: ${new Date().toLocaleString(currentLang)}
                                </div>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin: 8px 0 10px 0; padding: 5px 0; border-top: 1px solid ${pdfBorderColor}; border-bottom: 1px solid ${pdfBorderColor}; ${isRTL ? 'flex-direction: row-reverse;' : ''}">
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${reportIdLabel}:</strong> #${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${generatedByLabel}:</strong> ${escapeHtml(user?.fullname || user?.username || 'System')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${shopCodeLabel}:</strong> ${escapeHtml(currentShop?.shop_code || 'N/A')}</div>
                            </div>
                        `;

                        const hasFilters = searchTerm || startDate || endDate;
                        const filterHTML = hasFilters ? `
                            <div style="margin: 6px 0; padding: 4px 10px; background: #f7f7f7; border-${isRTL ? 'right' : 'left'}: 3px solid ${pdfThemeColor}; font-size: 6.5px; color: ${pdfSubTextColor};">
                                <span style="font-weight: 600;">${filtersAppliedLabel}:</span> 
                                ${searchTerm ? `${searchLabel}: "${escapeHtml(searchTerm)}"` : ''}
                                ${startDate ? ` | ${fromLabel}: ${startDate}` : ''}
                                ${endDate ? ` | ${toLabel}: ${endDate}` : ''}
                            </div>
                        ` : '';

                        let tableHTML = `
                            <table style="width: 100%; border-collapse: collapse; font-size: 6.5px; margin-top: 4px;">
                                <thead>
                                    <tr style="background-color: ${pdfHeaderBgColor}; border-bottom: 2px solid ${pdfThemeColor};">
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%;">#</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 12%;">${returnNumberLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${dateLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 18%;">${customerNameLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 8%;">${itemsLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${grandTotalLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${refundAmountLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 12%;">${returnReasonLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; width: 8%;">${addedByLabel}</th>
                                    </tr>
                                </thead>
                                <tbody>
                        `;

                        pageReturns.forEach((ret, idx) => {
                            const rowNum = startRowIndex + idx + 1;
                            const isEven = idx % 2 === 0;
                            
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${isEven ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${rowNum}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: #dc2626; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(ret.return_number) || '-'}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${formatDateForPDF(ret.date)}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(ret.customer_name) || (translations.walkInCustomer || 'Walk-in')}${ret.customer_phone ? `<div style="font-size: 5.5px; color: ${pdfSubTextColor};">${escapeHtml(ret.customer_phone)}</div>` : ''}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${ret.item_count || 0}</td>
                                    <td style="padding: 3px; text-align: right; color: #059669; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(ret.grand_total)}</td>
                                    <td style="padding: 3px; text-align: right; color: #dc2626; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(ret.refund_amount)}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(ret.return_reason) || '-'}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor};">${escapeHtml(ret.username) || '—'}</td>
                                </tr>
                            `;
                        });

                        const emptyRowsNeeded = rowsPerPage - pageReturns.length;
                        for (let i = 0; i < emptyRowsNeeded; i++) {
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${(pageReturns.length + i) % 2 === 0 ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px;">&nbsp;</td>
                                </tr>
                            `;
                        }

                        tableHTML += `
                                    <tr style="border-top: 1px solid ${pdfBorderColor}; background-color: #f8f8f8;">
                                        <td colspan="5" style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700;">Page Summary</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #059669;">${formatCurrencyForPDF(totalGrandTotal)}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #dc2626;">${formatCurrencyForPDF(totalRefundAmount)}</td>
                                        <td colspan="2">&nbsp;</td>
                                    </tr>
                                </tbody>
                            </table>
                        `;

                        const footerHTML = `
                            <div style="margin-top: 8px; padding-top: 5px; text-align: center;">
                                <div style="width: 30px; height: 1px; background: ${pdfBorderColor}; margin: 0 auto 5px auto;"></div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};">${pageLabel} ${pageNum} ${ofLabel} ${totalPages}</div>
                                <div style="font-size: 5px; color: #aaa; margin-top: 3px;">${translations.poweredBy || 'Powered by OurZest'}</div>
                            </div>
                        `;

                        container.innerHTML = headerHTML + filterHTML + tableHTML + footerHTML;
                        document.body.appendChild(container);
                        html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false }).then(canvas => {
                            document.body.removeChild(container);
                            resolve(canvas);
                        }).catch(err => { document.body.removeChild(container); reject(err); });
                    } catch (err) { reject(err); }
                });
            };

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, returnsToExport.length);
                const pageReturns = returnsToExport.slice(startRow, endRow);
                const canvas = await renderPage(pageReturns, pageNum, startRow);
                if (pageNum > 1) {
                    pdf.addPage();
                }
                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);
            }

            const fileName = `Returns_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(fileName);
            showToast('success', `${translations.reportGenerated || 'Report generated!'} ${returnsToExport.length} ${translations.returns || 'returns'} on ${totalPages} ${translations.pages || 'page(s)'}`);
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredReturns, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange, pdfStartPage, pdfEndPage, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, searchTerm, startDate, endDate, currentTheme]);

    const loadReturnDetails = async (returnRecord) => {
        const normalizeItems = (record) => {
            const source = Array.isArray(record?.items)
                ? record.items
                : (Array.isArray(record?.return_items) ? record.return_items : []);

            return source.map((item, index) => {
                const quantity = Number(item.quantity ?? item.return_quantity ?? item.return_qty ?? 0) || 0;
                const price = Number(item.price ?? item.unit_price ?? item.sell_price ?? 0) || 0;
                return {
                    ...item,
                    id: item.id ?? item.return_item_id ?? index,
                    sell_item_id: item.sell_item_id ?? item.sale_item_id ?? '',
                    product_id: item.product_id ?? '',
                    product_name: item.product_name || item.productName || item.name || 'Product',
                    product_code: item.product_code || '',
                    model: item.model || '',
                    serial_no: item.serial_no || '',
                    company: item.company || item.company_name || '',
                    batch_no: item.batch_no || item.batch || '',
                    barcode: item.barcode || '',
                    unit: item.unit || item.unit_of_measure || '',
                    quantity,
                    price,
                    total: Number(item.total ?? item.line_total ?? (quantity * price)) || 0,
                    available_for_return: Number(item.available_for_return ?? item.sold_quantity ?? quantity) || quantity
                };
            });
        };

        const normalizeSaleItems = (record) => {
            const source = Array.isArray(record?.sale_items) ? record.sale_items : [];
            return source.map((item, index) => ({
                ...item,
                id: item.id ?? item.sell_item_id ?? index,
                sell_item_id: item.sell_item_id ?? item.id ?? '',
                product_id: item.product_id ?? '',
                product_name: item.product_name || item.catalog_product_name || item.productName || 'Product',
                product_code: item.product_code || item.catalog_product_code || '',
                model: item.model || '',
                serial_no: item.serial_no || '',
                company: item.company || item.company_name || '',
                batch_no: item.batch_no || item.catalog_batch_no || '',
                barcode: item.barcode || item.catalog_barcode || '',
                unit: item.unit || item.catalog_unit || item.unit_of_measure || '',
                price: Number(item.price ?? item.sell_price ?? 0) || 0,
                available_for_return: Number(item.available_for_return ?? item.quantity ?? 0) || 0
            }));
        };

        if (returnRecord?.items?.length && Array.isArray(returnRecord?.sale_items)) {
            return {
                ...returnRecord,
                items: normalizeItems(returnRecord),
                sale_items: normalizeSaleItems(returnRecord)
            };
        }
        if (!returnRecord?.id || !currentShop?.shop_id) {
            return { ...returnRecord, items: normalizeItems(returnRecord) };
        }

        const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ action: 'get_record', id: returnRecord.id, shop_id: currentShop.shop_id })
        });

        const responseText = await response.text();
        let data;
        try {
            data = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
            console.error('Invalid return-detail response:', responseText);
            throw new Error(`The return server returned an invalid response (HTTP ${response.status})`);
        }
        if (!response.ok) throw new Error(data?.message || `Could not load return details (HTTP ${response.status})`);
        if (!data || data.status !== 'success' || !data.data) throw new Error(data?.message || 'Return details not found');
        return {
            ...returnRecord,
            ...data.data,
            items: normalizeItems(data.data),
            sale_items: normalizeSaleItems(data.data)
        };
    };

    const handlePrintReceipt = async (returnRecord) => {
        // Open synchronously from the click event so the browser does not block it.
        const printWindow = window.open('', '_blank');
        if (!printWindow) { showToast('error', 'Please allow popups for printing'); return; }
        printWindow.document.write('<!DOCTYPE html><html><head><title>Loading receipt...</title></head><body style="font-family:Arial;padding:24px">Preparing receipt...</body></html>');
        let returnData;
        try {
            returnData = await loadReturnDetails(returnRecord);
        } catch (error) {
            console.error('Error loading return receipt:', error);
            printWindow.close();
            showToast('error', 'Could not load return details for printing');
            return;
        }
        if (!returnData?.items?.length) {
            printWindow.close();
            showToast('warning', 'No returned items to print');
            return;
        }

        // Refresh business data at print time, matching SolarReturn.js. This avoids
        // printing before the component-level business request has completed.
        let printBusinessInfo = businessInfo || {};
        if (user?.username) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                    method: 'GET', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, credentials: 'include'
                });
                const data = await response.json();
                if (data.status === 'success') {
                    printBusinessInfo = {
                        businessname: data.businessname || '', bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo_url || '', businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '', email: data.email || '', phonenumber: data.phonenumber || '',
                        gst_number: data.gst_number || ''
                    };
                    if (isMountedRef.current) setBusinessInfo(printBusinessInfo);
                }
            } catch (error) {
                console.error('Could not refresh business information for receipt:', error);
            }
        }

        const businessName = printBusinessInfo?.businessname || user?.business_name || user?.fullname || currentShop?.shop_name || 'OurZest Store';
        const rawBusinessLogo = printBusinessInfo?.bussinesslogo_url || printBusinessInfo?.bussinesslogo || user?.bussinesslogo_url || user?.bussinesslogo || '';
        const businessLogo = rawBusinessLogo && !/^(https?:|data:|blob:)/i.test(rawBusinessLogo)
            ? `https://ourzestapp.online/oz_backend/${String(rawBusinessLogo).replace(/^\/+/, '')}`
            : rawBusinessLogo;
        const businessAddress = printBusinessInfo?.businessaddress || user?.business_address || '';
        const businessPhone = printBusinessInfo?.phonenumber || user?.phonenumber || '';
        const businessEmail = printBusinessInfo?.email || user?.email || '';
        const businessGst = printBusinessInfo?.gst_number || user?.gst_number || '';
        const customerName = returnData.customer_name || 'Walk-in Customer';
        const customerPhone = returnData.customer_phone || '';
        const returnNumber = returnData.return_number || returnData.id || '';
        const sizes = { a4: 'A4', a5: 'A5', letter: 'Letter', legal: 'Legal' };
        const printPageSize = sizes[String(pdfPageSize || 'a4').toLowerCase()] || 'A4';
        const money = value => `Rs. ${Math.round(Number.parseFloat(value) || 0)}`;
        const safe = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
        const businessWords = String(businessName).trim().split(/\s+/).filter(Boolean);
        const secondaryWords = businessWords.slice(2);
        const printBusinessName = { primary: businessWords.slice(0, 2).join(' '), secondary: secondaryWords.join(' '), secondaryUnits: secondaryWords.length === 1 ? Array.from(secondaryWords[0]) : secondaryWords };
        const date = returnData.date ? new Date(returnData.date) : new Date();
        const receiptDate = (Number.isNaN(date.getTime()) ? new Date() : date).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        printWindow.document.open();
        printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Return Receipt ${safe(returnNumber)}</title><style>
@page{size:${printPageSize} portrait;margin:12mm}*{box-sizing:border-box}:root{--ink:#000;--muted:#444;--line:#b8b8b8}
body{margin:0;color:var(--ink);background:#fff;font:10pt/1.45 'Segoe UI',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}.receipt{width:100%}.header{text-align:left}.brand{display:flex;align-items:center;justify-content:flex-start;gap:18px;min-height:140px;margin-left:-18px;padding:14px 4px 22px}.logo{flex:0 0 auto;width:158px;max-width:158px;max-height:112px;object-fit:contain;filter:grayscale(100%) contrast(115%)}.business-copy{min-width:0;text-align:left}.business-name-lockup{display:inline-flex;flex-direction:column;width:max-content;max-width:100%}.business-name-primary{font-size:28pt;font-weight:850;line-height:1.02}.business-name-secondary{display:flex;justify-content:space-between;width:100%;margin-top:4px;font-size:17pt;font-weight:650;line-height:1.1}.business-detail{margin-top:5px;color:var(--muted);font-size:10.5pt;line-height:1.3}.contact-line{display:flex;justify-content:flex-start;flex-wrap:nowrap;gap:3px 18px;white-space:nowrap}
.receipt-heading{padding:8px 18px 9px;background:#f7f7f7;border:1px solid #888;border-radius:3px 3px 0 0}.receipt-title{font-size:15pt;font-weight:800;letter-spacing:2.5px;text-transform:uppercase}.meta{display:grid;grid-template-columns:1.25fr .9fr 1fr;margin:0 0 10px;border:1px solid #888;border-top:0;border-radius:0 0 3px 3px;overflow:hidden}.meta>span{min-height:46px;padding:7px 10px;border-right:1px solid #aaa;display:flex;flex-direction:column;justify-content:center}.meta>span:last-child{border-right:0}.meta strong,.return-info strong{color:var(--muted);font-size:7.5pt;letter-spacing:.6px;text-transform:uppercase}.meta-value{font-weight:650;overflow-wrap:anywhere}.meta-subvalue{margin-top:1px;color:var(--muted);font-size:8pt}.return-info{display:grid;grid-template-columns:.8fr 1.2fr;margin-bottom:14px;border:1px solid var(--line);border-radius:3px;overflow:hidden}.return-info>div{min-height:40px;padding:6px 10px;border-right:1px solid var(--line);display:flex;flex-direction:column;justify-content:center}.return-info>div:last-child{border-right:0}
table{width:100%;margin:0 0 16px;border-collapse:separate;border-spacing:0;border:1px solid var(--line);border-radius:3px;overflow:hidden}thead{display:table-header-group}th{background:#f7f7f7;font-size:7.5pt;font-weight:700;letter-spacing:.4px;text-transform:uppercase}th,td{padding:5px 6px;border:0;border-right:1px solid var(--line);border-bottom:1px solid var(--line);line-height:1.2;vertical-align:middle}th:last-child,td:last-child{border-right:0}tbody tr:last-child td{border-bottom:0}tbody tr:nth-child(even){background:#fafafa}th:not(:nth-child(2)),td:not(:nth-child(2)){text-align:center}th:nth-child(2),td:nth-child(2){text-align:left}tr{break-inside:avoid;page-break-inside:avoid}.product{font-weight:650}.item-meta{display:block;color:var(--muted);font-size:7pt}
.payment-section{display:flex;justify-content:flex-end;break-inside:avoid}.payment-box{width:310px;border:1px solid var(--line);border-radius:3px;overflow:hidden}.payment-row{display:flex;justify-content:space-between;gap:20px;padding:5px 12px}.payment-row:first-child{padding-top:9px}.payment-row.total,.payment-row.refund{margin:4px 0;padding:8px 12px;background:#f7f7f7;border-top:1px solid #999;border-bottom:1px solid #999;font-size:12pt;font-weight:800}.payment-row.refund{margin-bottom:0;border-bottom:0}.footer{margin-top:22px;padding-top:12px;border-top:1px solid var(--line);color:var(--muted);font-size:8pt;text-align:center}.thanks{color:var(--ink);font-size:11pt;font-weight:700}
@media print and (max-width:160mm){body{font-size:8.5pt}.business-name-primary{font-size:22pt}.business-name-secondary{font-size:13.5pt}.receipt-title{font-size:12pt}.brand{min-height:106px;margin-left:-12px;gap:14px;padding:8px 2px 14px}.logo{width:112px;max-width:112px;max-height:80px}th,td{padding:4px;font-size:7pt}.payment-box{width:245px}.meta>span{min-height:40px;padding:5px 6px}.meta{grid-template-columns:1.15fr .9fr 1fr}.meta strong,.return-info strong{font-size:6.5pt}.meta-value{font-size:8pt}.meta-subvalue{font-size:7pt}}
</style></head><body><main class="receipt"><header class="header"><div class="brand">${businessLogo ? `<img class="logo" src="${safe(businessLogo)}" alt="${safe(businessName)}" onerror="this.style.display='none'">` : ''}<div class="business-copy"><div class="business-name-lockup"><div class="business-name-primary">${safe(printBusinessName.primary)}</div>${printBusinessName.secondary ? `<div class="business-name-secondary">${printBusinessName.secondaryUnits.map(unit => `<span>${safe(unit)}</span>`).join('')}</div>` : ''}</div><div class="contact-line">${businessAddress ? `<div class="business-detail">${safe(businessAddress)}</div>` : ''}${businessPhone ? `<div class="business-detail">Phone: ${safe(businessPhone)}</div>` : ''}</div>${businessGst ? `<div class="business-detail">GST: ${safe(businessGst)}</div>` : ''}</div></div><div class="receipt-heading"><div class="receipt-title">${translations.returnReceipt || 'Return Receipt'}</div></div></header>
<section class="meta"><span><strong>${translations.customer || 'Customer'}</strong><span class="meta-value">${safe(customerName)}</span>${customerPhone ? `<small class="meta-subvalue">${translations.phone || 'Phone'}: ${safe(customerPhone)}</small>` : ''}</span><span><strong>${translations.returnNumber || 'Return No.'}</strong><span class="meta-value">#${safe(returnNumber)}</span></span><span><strong>${translations.dateTime || 'Date & Time'}</strong><span class="meta-value">${safe(receiptDate)}</span></span></section>
${(returnData.original_bill_number || returnData.return_reason) ? `<section class="return-info"><div><strong>${translations.originalInvoice || 'Original Invoice'}</strong><span class="meta-value">${safe(returnData.original_bill_number || '—')}</span></div><div><strong>${translations.returnReason || 'Return Reason'}</strong><span class="meta-value">${safe(returnData.return_reason || '—')}</span></div></section>` : ''}
<table><thead><tr><th style="width:5%">#</th><th style="width:43%">${translations.productName || 'Product'}</th><th style="width:14%">${translations.quantity || 'Qty'}</th><th style="width:18%">${translations.price || 'Price'}</th><th style="width:20%">${translations.total || 'Total'}</th></tr></thead><tbody>${returnData.items.map((item,index)=>`<tr><td>${index+1}</td><td><div class="product">${safe(item.product_name || 'Product')}</div>${item.unit?`<small class="item-meta">${safe(item.unit)}</small>`:''}</td><td>${Math.round(Number(item.quantity)||0)}</td><td>${money(item.price)}</td><td>${money(item.total ?? Number(item.price)*Number(item.quantity))}</td></tr>`).join('')}</tbody></table>
<section class="payment-section"><div class="payment-box"><div class="payment-row"><span>${translations.subtotal || 'Subtotal'}</span><span>${money(returnData.subtotal)}</span></div>${Number(returnData.tax)>0?`<div class="payment-row"><span>${translations.tax || 'Tax'}</span><span>${money(returnData.tax)}</span></div>`:''}${Number(returnData.discount)>0?`<div class="payment-row"><span>${translations.discount || 'Discount'}</span><span>-${money(returnData.discount)}</span></div>`:''}<div class="payment-row total"><span>${translations.grandTotal || 'Grand Total'}</span><span>${money(returnData.grand_total)}</span></div><div class="payment-row refund"><span>${translations.refundAmount || 'Refund Amount'}</span><span>${money(returnData.refund_amount)}</span></div></div></section><footer class="footer"><div class="thanks">${translations.thankYou || 'Thank you for your business!'}</div><div>Developed by OurZest PVT LTD.</div></footer></main><script>window.onload=async function(){await Promise.all(Array.from(document.images).map(img=>img.complete?Promise.resolve():new Promise(resolve=>{img.onload=resolve;img.onerror=resolve})));if(document.fonts&&document.fonts.ready)await document.fonts.ready;window.onafterprint=function(){window.close()};window.print()};</script></body></html>`);
        printWindow.document.close();
        printWindow.focus();
    };

    const recalculateEditedReturn = useCallback((items, taxValue, discountValue, refundValue = null) => {
        const subtotal = (items || []).reduce(
            (sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 0)),
            0
        );
        const tax = Number(taxValue) || 0;
        const discount = Number(discountValue) || 0;
        const grandTotal = Math.max(0, subtotal + tax - discount);
        const requestedRefund = refundValue === null || refundValue === ''
            ? grandTotal
            : Number(refundValue) || 0;
        return {
            subtotal: String(Math.round(subtotal)),
            grand_total: String(Math.round(grandTotal)),
            refund_amount: String(Math.round(Math.min(Math.max(0, requestedRefund), grandTotal)))
        };
    }, []);

    const handleEdit = async (returnRecord) => {
        const requestId = editDetailsRequestRef.current + 1;
        editDetailsRequestRef.current = requestId;
        const applyRecordToEditor = (record) => {
            const items = Array.isArray(record.items) ? record.items : [];
            const calculated = recalculateEditedReturn(
                items,
                record.tax || 0,
                record.discount || 0,
                record.refund_amount
            );
            setSelectedReturn(record);
            setEditSaleItems(Array.isArray(record.sale_items) ? record.sale_items : []);
            setEditItemErrors({});
            setEditItemToAdd('');
            setEditFormData({
                id: record.id,
                date: String(record.date || '').slice(0, 10),
                customer_name: record.customer_name || '', customer_phone: record.customer_phone || '',
                payment_method: record.payment_method || 'cash', return_reason: record.return_reason || '', notes: record.notes || '',
                subtotal: calculated.subtotal, tax: String(Math.round(Number(record.tax || 0))),
                discount: String(Math.round(Number(record.discount || 0))), grand_total: calculated.grand_total,
                refund_amount: calculated.refund_amount, items
            });
        };
        // Open immediately; then enrich the form with item/detail data if required.
        applyRecordToEditor(returnRecord);
        setShowEditModal(true);
        setEditDetailsLoading(true);
        try {
            const record = await loadReturnDetails(returnRecord);
            if (editDetailsRequestRef.current === requestId) applyRecordToEditor(record);
        } catch (error) {
            console.error('Error loading return for edit:', error);
            if (editDetailsRequestRef.current === requestId) {
                showToast('warning', error.message || 'Full return details could not be loaded');
            }
        } finally {
            if (editDetailsRequestRef.current === requestId) setEditDetailsLoading(false);
        }
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditFormData(previous => {
            const next = { ...previous, [name]: value };
            if (['subtotal', 'tax', 'discount'].includes(name)) {
                Object.assign(next, recalculateEditedReturn(
                    next.items,
                    next.tax,
                    next.discount
                ));
            } else if (name === 'refund_amount') {
                next.refund_amount = String(Math.round(Math.min(
                    Math.max(0, Number(value) || 0),
                    Number(next.grand_total) || 0
                )));
            }
            return next;
        });
    };

    const updateEditItem = useCallback((index, field, value) => {
        setEditFormData(previous => {
            const items = previous.items.map((item, itemIndex) => {
                if (itemIndex !== index) return item;
                if (['quantity', 'price'].includes(field)) {
                    if (value !== '' && (Number.isNaN(Number(value)) || Number(value) < 0)) return item;
                }
                return {
                    ...item,
                    [field]: value,
                    total: field === 'quantity' || field === 'price'
                        ? (Number(field === 'price' ? value : item.price) || 0)
                            * (Number(field === 'quantity' ? value : item.quantity) || 0)
                        : item.total
                };
            });
            return {
                ...previous,
                items,
                ...recalculateEditedReturn(items, previous.tax, previous.discount)
            };
        });
        setEditItemErrors(previous => {
            const next = { ...previous };
            delete next[`${field}_${index}`];
            delete next.items;
            return next;
        });
    }, [recalculateEditedReturn]);

    const removeEditItem = useCallback((index) => {
        setEditFormData(previous => {
            const items = previous.items.filter((_, itemIndex) => itemIndex !== index);
            return {
                ...previous,
                items,
                ...recalculateEditedReturn(items, previous.tax, previous.discount)
            };
        });
        setEditItemErrors({});
    }, [recalculateEditedReturn]);

    const addEditItem = useCallback((sellItemId) => {
        const sourceItem = editSaleItems.find(
            item => String(item.sell_item_id ?? item.id) === String(sellItemId)
        );
        if (!sourceItem) return;

        setEditFormData(previous => {
            const alreadyAdded = previous.items.some(
                item => String(item.sell_item_id) === String(sourceItem.sell_item_id ?? sourceItem.id)
            );
            if (alreadyAdded) return previous;

            const quantity = Math.min(1, Number(sourceItem.available_for_return) || 0);
            const item = {
                ...sourceItem,
                id: `new-${sourceItem.sell_item_id ?? sourceItem.id}`,
                sell_item_id: sourceItem.sell_item_id ?? sourceItem.id,
                quantity,
                price: Number(sourceItem.price) || 0,
                total: quantity * (Number(sourceItem.price) || 0)
            };
            const items = [...previous.items, item];
            return {
                ...previous,
                items,
                ...recalculateEditedReturn(items, previous.tax, previous.discount)
            };
        });
        setEditItemToAdd('');
        setEditItemErrors({});
    }, [editSaleItems, recalculateEditedReturn]);

    const editAddItemOptions = useMemo(() => {
        const selectedIds = new Set(editFormData.items.map(item => String(item.sell_item_id)));
        return editSaleItems
            .filter(item => !selectedIds.has(String(item.sell_item_id ?? item.id)))
            .filter(item => Number(item.available_for_return) > 0)
            .map(item => ({
                value: String(item.sell_item_id ?? item.id),
                label: `${item.product_name || 'Product'}${item.batch_no ? ` — ${item.batch_no}` : ''} (${Math.round(Number(item.available_for_return) || 0)} available)`
            }));
    }, [editSaleItems, editFormData.items]);

    const handleUpdateReturn = async (event) => {
        event.preventDefault();
        if (!editFormData.id || !currentShop || editDetailsLoading) return;

        const validationErrors = {};
        if (!editFormData.items.length) validationErrors.items = 'At least one returned item is required';
        editFormData.items.forEach((item, index) => {
            const quantity = Number(item.quantity) || 0;
            const maximum = Number(item.available_for_return) || 0;
            const price = Number(item.price);
            if (!item.sell_item_id) validationErrors[`item_${index}`] = 'Original sale item is missing';
            if (quantity <= 0) validationErrors[`quantity_${index}`] = 'Quantity must be greater than zero';
            else if (quantity > maximum) validationErrors[`quantity_${index}`] = `Maximum available quantity is ${maximum}`;
            if (Number.isNaN(price) || price < 0) validationErrors[`price_${index}`] = 'Enter a valid price';
        });
        if (Object.keys(validationErrors).length) {
            setEditItemErrors(validationErrors);
            showToast('error', validationErrors.items || 'Please correct the highlighted returned items');
            return;
        }

        setEditLoading(true);
        try {
            const items = editFormData.items.map(item => ({
                sell_item_id: Number(item.sell_item_id),
                product_id: Number(item.product_id),
                product_name: String(item.product_name || '').trim(),
                product_code: String(item.product_code || '').trim(),
                model: String(item.model || '').trim(),
                serial_no: String(item.serial_no || '').trim(),
                company: String(item.company || '').trim(),
                batch_no: String(item.batch_no || '').trim(),
                barcode: String(item.barcode || '').trim(),
                unit: String(item.unit || '').trim(),
                quantity: Number(item.quantity),
                price: Number(item.price),
                total: (Number(item.quantity) || 0) * (Number(item.price) || 0)
            }));
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update', id: editFormData.id, return_id: editFormData.id, record_id: editFormData.id, shop_id: currentShop.shop_id,
                    username: user?.username || '', return_number: selectedReturn?.return_number || '',
                    original_bill_number: selectedReturn?.original_bill_number || '', items,
                    date: editFormData.date, customer_name: editFormData.customer_name.trim(),
                    customer_phone: editFormData.customer_phone.trim(), payment_method: editFormData.payment_method,
                    return_reason: editFormData.return_reason.trim(), notes: editFormData.notes.trim(),
                    subtotal: Number(editFormData.subtotal) || 0, tax: Number(editFormData.tax) || 0,
                    discount: Number(editFormData.discount) || 0, grand_total: Number(editFormData.grand_total) || 0,
                    refund_amount: Number(editFormData.refund_amount) || 0
                })
            });
            const data = await response.json();
            if (data.status !== 'success') throw new Error(data.message || 'Update failed');
            setShowEditModal(false);
            showToast('success', data.message || 'Return updated successfully');
            await fetchReturns();
        } catch (error) {
            console.error('Error updating return:', error);
            showToast('error', error.message || 'Could not update return');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (returnRecord) => {
        if (!returnRecord?.id || !currentShop) return;
        const confirmed = window.confirm(`Delete return #${returnRecord.return_number || returnRecord.id}? This action cannot be undone.`);
        if (!confirmed) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete', id: returnRecord.id, return_id: returnRecord.id, record_id: returnRecord.id,
                    shop_id: currentShop.shop_id, username: user?.username || ''
                })
            });
            const data = await response.json();
            if (data.status !== 'success') throw new Error(data.message || 'Delete failed');
            if (selectedReturn?.id === returnRecord.id) {
                setShowViewModal(false);
                setShowEditModal(false);
                setSelectedReturn(null);
            }
            showToast('success', data.message || 'Return deleted successfully');
            await fetchReturns();
        } catch (error) {
            console.error('Error deleting return:', error);
            showToast('error', error.message || 'Could not delete return');
        }
    };

    const handleView = async (returnRecord) => {
        setSelectedReturn(returnRecord);
        
        if ((!returnRecord.items || returnRecord.items.length === 0) && returnRecord.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: returnRecord.id,
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedReturn(data.data);
                }
            } catch (error) {
                console.error('Error fetching return details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handlePageChange = useCallback((page) => { if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); }, [pagination.totalPages]);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozlistui-shop-indicator no-shop">
                    <div className="ozlistui-shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="ozlistui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="ozlistui-btn ozlistui-btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="ozlistui-shop-indicator active">
                {/* <div className="ozlistui-shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="ozlistui-shop-details">
                        <span className="ozlistui-shop-name">{currentShop.shop_name}</span>
                        <span className="ozlistui-shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="ozlistui-shop-info-note">
                    {translations.willShowFromShop || 'Showing returns from this shop'}
                </div>
                <button
                    className="ozlistui-btn ozlistui-btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button> */}
            </div>
        );
    };

    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
                darkMode={darkMode}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{translations.returnsList || 'Returns List'}</h1>
                            <p>{translations.formDetails || 'All return details are below'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button 
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                        </button>
                        
                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {renderShopIndicator()}

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    placeholder=" "
                                />
                                <label htmlFor="startDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    {translations.startDate || 'Start Date'}
                                </label>
                            </div>
                        </div>
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    placeholder=" "
                                />
                                <label htmlFor="endDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    {translations.endDate || 'End Date'}
                                </label>
                            </div>
                        </div>

                        <div className="ozlistui-filter-group search-group">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="text"
                                    id="searchTerm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder=" "
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="searchTerm">
                                    <FontAwesomeIcon icon={faSearch} />
                                    {translations.searchPlaceholder || 'Search returns...'}
                                </label>
                            </div>
                        </div>

                        <CustomDropdown
                            label={translations.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label={translations.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label="Page Range"
                            icon={faFilePdf}
                            value={pdfPageRange}
                            onChange={setPdfPageRange}
                            options={pdfPageRangeOptions}
                            translations={translations}
                        />

                        {pdfPageRange === 'range' && (
                            <>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input 
                                            type="number" 
                                            min="1" 
                                            value={pdfStartPage} 
                                            onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                            placeholder=" "
                                        />
                                        <label>From</label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input 
                                            type="number" 
                                            min={pdfStartPage} 
                                            value={pdfEndPage} 
                                            onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                            placeholder=" "
                                        />
                                        <label>To</label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchReturns} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredReturns.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                            </button>
                        </div>

                        {(searchTerm || startDate || endDate) && (
                            <div className="ozlistui-filter-group">
                                <button onClick={resetFilters} className="ozlistui-clear-filters-btn">
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations.clear || 'Clear'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{translations.loadingUserData || 'Loading your return data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view returns'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="ozlistui-centered-cell">{translations.returnNumber || 'Return #'}</th>
                                            <th className="ozlistui-centered-cell">{translations.date || 'Date'}</th>
                                            <th className="ozlistui-centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="ozlistui-centered-cell">{translations.items || 'Items'}</th>
                                            <th className="ozlistui-centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="ozlistui-centered-cell">{translations.refundAmount || 'Refund'}</th>
                                            <th className="ozlistui-centered-cell">{translations.returnReason || 'Reason'}</th>
                                            <th className="ozlistui-centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentReturns.map((ret, index) => {
                                            const addedBy = ret.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            
                                            return (
                                                <tr key={ret.id}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text ozlistui-return-number" style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#dc2626' }}>{ret.return_number}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{formatDate(ret.date)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <div className="ozlistui-customer-info-cell">
                                                            <span className="ozlistui-cell-text">{ret.customer_name || (translations.walkInCustomer || 'Walk-in')}</span>
                                                            {ret.customer_phone && <div className="ozlistui-cell-subtext"><FontAwesomeIcon icon={faPhone} size="xs" /> {ret.customer_phone}</div>}
                                                        </div>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <div className="ozlistui-items-badge">
                                                            <span>{ret.item_count || 0}</span>
                                                        </div>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(ret.grand_total)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#dc2626', fontWeight: 'bold' }}>{formatCurrency(ret.refund_amount)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text ozlistui-reason-text">{ret.return_reason || '-'}</span>
                                                    </td>
                                                                                                        <td className="ozlistui-centered-cell">
                                                        <span className={`ozlistui-added-by-badge ${isSelfAdded ? 'ozlistui-self' : ''}`}>
                                                            {addedBy}
                                                        </span>
                                                     </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell">
                                                        <div className="ozlistui-actions-content">
                                                            <button onClick={() => handleView(ret)} className="ozlistui-action-btn ozlistui-view-btn" title={translations.view || 'View'}>
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button onClick={() => handlePrintReceipt(ret)} className="ozlistui-action-btn ozlistui-print-btn" title={translations.print || 'Print Receipt'}>
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button onClick={() => handleEdit(ret)} className="ozlistui-action-btn ozlistui-edit-btn" title={translations.edit || 'Edit'}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button onClick={() => handleDelete(ret)} className="ozlistui-action-btn ozlistui-delete-btn" title={translations.delete || 'Delete'}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                     </td>
                                                 </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredReturns.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-info">
                                <span>{translations.showing || 'Showing'} {pagination.startIndex + 1} {translations.of || 'of'} {filteredReturns.length} {translations.returns || 'returns'}</span>
                                <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="ozlistui-items-per-page">
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div className="ozlistui-pagination-controls">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={() => !editLoading && setShowEditModal(false)}>
                    <div className="ozlistui-modal-content ozlistui-edit-modal-content" style={{ width: '95vw', maxWidth: 1450, maxHeight: '92vh' }} onClick={(event) => event.stopPropagation()}>
                        <form className="ozlistui-edit-form" onSubmit={handleUpdateReturn}>
                            <div className="ozlistui-modal-header">
                                <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                                <div className="ozlistui-modal-title">
                                    <h3>{translations.editReturn || 'Edit Return'}</h3>
                                    <span>#{selectedReturn?.return_number || ''}</span>
                                </div>
                                <button type="button" className="ozlistui-close-btn" disabled={editLoading} onClick={() => setShowEditModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                            </div>
                            <div className="ozlistui-modal-body ozlistui-edit-modal-body" style={{ overflowY: 'auto' }}>
                                <div className="ozlistui-view-grid ozlistui-form-grid" style={{ alignItems: 'start' }}>
                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input id="edit_return_date" type="date" name="date" required value={editFormData.date} onChange={handleEditChange} placeholder=" " />
                                            <label htmlFor="edit_return_date"><FontAwesomeIcon icon={faCalendar} /> {translations.date || 'Date'} <span className="ozlistui-required">*</span></label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input id="edit_return_customer" name="customer_name" value={editFormData.customer_name} onChange={handleEditChange} placeholder=" " />
                                            <label htmlFor="edit_return_customer"><FontAwesomeIcon icon={faUser} /> {translations.customerName || 'Customer Name'}</label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input id="edit_return_phone" type="tel" name="customer_phone" value={editFormData.customer_phone} onChange={handleEditChange} placeholder=" " dir="ltr" />
                                            <label htmlFor="edit_return_phone"><FontAwesomeIcon icon={faPhone} /> {translations.phone || 'Phone'} <span>({translations.optional || 'Optional'})</span></label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <select id="edit_return_payment" name="payment_method" value={editFormData.payment_method} onChange={handleEditChange}>
                                                <option value="cash">Cash</option><option value="card">Card</option><option value="bank_transfer">Bank Transfer</option><option value="credit">Store Credit</option>
                                            </select>
                                            <label htmlFor="edit_return_payment"><FontAwesomeIcon icon={faCreditCard} /> {translations.refundMethod || 'Refund Method'}</label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea id="edit_return_reason" name="return_reason" rows="2" value={editFormData.return_reason} onChange={handleEditChange} placeholder=" " />
                                            <label htmlFor="edit_return_reason"><FontAwesomeIcon icon={faInfoCircle} /> {translations.returnReason || 'Return Reason'}</label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea id="edit_return_notes" name="notes" rows="2" value={editFormData.notes} onChange={handleEditChange} placeholder=" " />
                                            <label htmlFor="edit_return_notes"><FontAwesomeIcon icon={faReceipt} /> {translations.notes || 'Notes'} <span>({translations.optional || 'Optional'})</span></label>
                                        </div>
                                    </div>
                                    <div className="ozlistui-full-width" style={{ gridColumn: '1 / -1', width: '100%' }}>
                                        {editDetailsLoading ? (
                                            <div className="ozlistui-loading" style={{ minHeight: 100 }}>
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                <span>{translations.loading || 'Loading returned items...'}</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="ozlistui-view-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                    <FontAwesomeIcon icon={faBoxes} />
                                                    <span>{translations.items || 'Returned Items'}</span>
                                                </div>
                                                <div style={{ maxWidth: 560, marginBottom: 14 }}>
                                                    <CustomDropdown
                                                        label={translations.addItem || 'Add Item from Original Sale'}
                                                        icon={faPlus}
                                                        value={editItemToAdd}
                                                        onChange={(value) => {
                                                            setEditItemToAdd(value);
                                                            addEditItem(value);
                                                        }}
                                                        options={editAddItemOptions}
                                                        placeholder={editAddItemOptions.length ? 'Select an item to add' : 'All eligible sale items are already added'}
                                                        translations={translations}
                                                        disabled={editLoading || editAddItemOptions.length === 0}
                                                    />
                                                </div>
                                                {editFormData.items.length > 0 ? (
                                                    <div className="ozlistui-items-table-container ozlistui-edit-items-scroll-region">
                                                        <div className="ozlistui-items-table-wrapper" style={{ minWidth: 1500 }}>
                                                    <table className="ozlistui-items-table">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>{translations.productName || 'Product'}</th>
                                                                <th>{translations.model || 'Model'}</th>
                                                                <th>{translations.serialNo || 'Serial No.'}</th>
                                                                <th>{translations.batch || 'Batch'}</th>
                                                                <th>{translations.unit || 'Unit'}</th>
                                                                <th className="ozlistui-text-right">{translations.price || 'Price'}</th>
                                                                <th className="ozlistui-text-right">{translations.quantity || 'Qty'}</th>
                                                                <th className="ozlistui-text-right">{translations.available || 'Available'}</th>
                                                                <th className="ozlistui-text-right">{translations.total || 'Total'}</th>
                                                                <th>{translations.actions || 'Actions'}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {editFormData.items.map((item, index) => (
                                                                <tr key={item.id ?? `${item.product_id || 'item'}-${index}`}>
                                                                    <td>{index + 1}</td>
                                                                    <td><input value={item.product_name || ''} onChange={(event) => updateEditItem(index, 'product_name', event.target.value)} disabled={editLoading} /></td>
                                                                    <td><input value={item.model || ''} onChange={(event) => updateEditItem(index, 'model', event.target.value)} disabled={editLoading} /></td>
                                                                    <td><input value={item.serial_no || ''} onChange={(event) => updateEditItem(index, 'serial_no', event.target.value)} disabled={editLoading} /></td>
                                                                    <td><input value={item.batch_no || ''} onChange={(event) => updateEditItem(index, 'batch_no', event.target.value)} disabled={editLoading} /></td>
                                                                    <td><input value={item.unit || ''} onChange={(event) => updateEditItem(index, 'unit', event.target.value)} disabled={editLoading} /></td>
                                                                    <td className="ozlistui-text-right">
                                                                        <input type="number" min="0" step="any" value={item.price} onChange={(event) => updateEditItem(index, 'price', event.target.value)} disabled={editLoading} className={editItemErrors[`price_${index}`] ? 'ozlistui-error' : ''} />
                                                                        {editItemErrors[`price_${index}`] && <small className="ozlistui-error-message">{editItemErrors[`price_${index}`]}</small>}
                                                                    </td>
                                                                    <td className="ozlistui-text-right">
                                                                        <input type="number" min="0.01" max={item.available_for_return} step="any" value={item.quantity} onChange={(event) => updateEditItem(index, 'quantity', event.target.value)} disabled={editLoading} className={editItemErrors[`quantity_${index}`] ? 'ozlistui-error' : ''} />
                                                                        {editItemErrors[`quantity_${index}`] && <small className="ozlistui-error-message">{editItemErrors[`quantity_${index}`]}</small>}
                                                                    </td>
                                                                    <td className="ozlistui-text-right">{Math.round(Number(item.available_for_return) || 0)}</td>
                                                                    <td className="ozlistui-text-right">{formatCurrency(item.total ?? ((Number(item.price) || 0) * (Number(item.quantity) || 0)))}</td>
                                                                    <td>
                                                                        <button type="button" className="ozlistui-action-btn ozlistui-delete-btn" onClick={() => removeEditItem(index)} disabled={editLoading} title={translations.remove || 'Remove'}>
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="ozlistui-empty-state" style={{ minHeight: 100 }}>
                                                        <FontAwesomeIcon icon={faBoxes} />
                                                        <p>{translations.noItems || 'No returned items. Add at least one item from the original sale.'}</p>
                                                    </div>
                                                )}
                                                {editItemErrors.items && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {editItemErrors.items}</div>}
                                            </>
                                        )}
                                    </div>
                                    <div className="ozlistui-edit-financial-summary ozlistui-full-width" style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, width: '100%' }}>
                                        {['subtotal', 'tax', 'discount', 'grand_total', 'refund_amount'].map(field => (
                                            <div key={field} className="ozlistui-form-group">
                                                <div className="ozlistui-input-wrapper">
                                                    <input id={`edit_return_${field}`} type="number" min="0" step="any" name={field} value={editFormData[field]} onChange={handleEditChange} readOnly={field === 'subtotal' || field === 'grand_total'} className={field === 'subtotal' || field === 'grand_total' ? 'ozlistui-readonly-input' : ''} placeholder="0" />
                                                    <label htmlFor={`edit_return_${field}`}><FontAwesomeIcon icon={faDollarSign} /> {{ subtotal: 'Subtotal', tax: 'Tax', discount: 'Discount', grand_total: 'Grand Total', refund_amount: 'Refund Amount' }[field]}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" disabled={editLoading} onClick={() => setShowEditModal(false)}>{translations.cancel || 'Cancel'}</button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading || editDetailsLoading}>
                                    <FontAwesomeIcon icon={(editLoading || editDetailsLoading) ? faSpinner : faSave} spin={editLoading || editDetailsLoading} /> {editLoading ? (translations.updating || 'Updating...') : editDetailsLoading ? (translations.loading || 'Loading...') : (translations.saveChanges || 'Save Changes')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && selectedReturn && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={() => setShowViewModal(false)}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon" style={{ background: '#dc2626' }}>
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.returnDetails || 'Return Details'}</h3>
                                <span className="ozlistui-return-number-badge" style={{ color: '#dc2626' }}>#{selectedReturn.return_number}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faInfoCircle} />Return Information</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.returnNumber || 'Return Number'}:</span><span className="ozlistui-view-value" style={{ color: '#dc2626', fontWeight: 'bold' }}>{selectedReturn.return_number}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.date || 'Date'}:</span><span className="ozlistui-view-value">{formatDate(selectedReturn.date)}</span></div>
                                    {selectedReturn.original_bill_number && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.originalInvoice || 'Original Invoice'}:</span><span className="ozlistui-view-value">{selectedReturn.original_bill_number}</span></div>
                                    )}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.customerName || 'Customer Name'}:</span><span className="ozlistui-view-value">{selectedReturn.customer_name || (translations.walkInCustomer || 'Walk-in Customer')}</span></div>
                                    {selectedReturn.customer_phone && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.phone || 'Phone'}:</span><span className="ozlistui-view-value">{selectedReturn.customer_phone}</span></div>
                                    )}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.refundMethod || 'Refund Method'}:</span><span className="ozlistui-view-value">{(selectedReturn.payment_method || 'CASH').toUpperCase()}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.addedBy || 'Added By'}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge">{selectedReturn.username || '-'}</span></span></div>
                                    {selectedReturn.return_reason && (
                                        <div className="ozlistui-view-row ozlistui-full-width"><span className="ozlistui-view-label">{translations.returnReason || 'Return Reason'}:</span><span className="ozlistui-view-value">{selectedReturn.return_reason}</span></div>
                                    )}
                                    {selectedReturn.notes && (
                                        <div className="ozlistui-view-row ozlistui-full-width"><span className="ozlistui-view-label">{translations.notes || 'Notes'}:</span><span className="ozlistui-view-value">{selectedReturn.notes}</span></div>
                                    )}
                                </div>
                            </div>

                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faCalculator} />Payment Summary</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.subtotal || 'Subtotal'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedReturn.subtotal)}</span></div>
                                    {parseFloat(selectedReturn.tax) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.tax || 'Tax'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedReturn.tax)}</span></div>}
                                    {parseFloat(selectedReturn.discount) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.discount || 'Discount'}:</span><span className="ozlistui-view-value">-{formatCurrency(selectedReturn.discount)}</span></div>}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.grandTotal || 'Grand Total'}:</span><span className="ozlistui-view-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedReturn.grand_total)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.refundAmount || 'Refund Amount'}:</span><span className="ozlistui-view-value" style={{ color: '#dc2626', fontWeight: 'bold' }}>{formatCurrency(selectedReturn.refund_amount)}</span></div>
                                </div>
                            </div>

                            {selectedReturn.items && selectedReturn.items.length > 0 && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBoxes} />{translations.items || 'Returned Items'}</h4>
                                    <div className="ozlistui-items-table-container">
                                        <div className="ozlistui-items-table-wrapper">
                                            <table className="ozlistui-items-table">
                                                <thead>
                                                    <tr>
                                                        <th>{translations.serialNo || '#'}</th>
                                                        <th>{translations.productName || 'Product'}</th>
                                                        <th>{translations.itemBatch || 'Batch'}</th>
                                                        <th>{translations.itemUnit || 'Unit'}</th>
                                                        <th className="ozlistui-text-right">{translations.price || 'Price'}</th>
                                                        <th className="ozlistui-text-right">{translations.quantity || 'Qty'}</th>
                                                        <th className="ozlistui-text-right">{translations.total || 'Total'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedReturn.items.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{idx + 1}</td>
                                                            <td>{item.product_name || item.productName || 'Product'}</td>
                                                            <td>{item.batch_no || '-'}</td>
                                                            <td>{item.unit || '-'}</td>
                                                            <td className="ozlistui-text-right">{formatCurrency(item.price)}</td>
                                                            <td className="ozlistui-text-right">{Math.round(parseFloat(item.quantity) || 0)}</td>
                                                            <td className="ozlistui-text-right ozlistui-total-cell">{formatCurrency(item.total || item.price * item.quantity)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => handlePrintReceipt(selectedReturn)}><FontAwesomeIcon icon={faPrint} /> {translations.print || 'Print Receipt'}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedReturn); }}><FontAwesomeIcon icon={faEdit} /> {translations.edit || 'Edit'}</button>
                            <button className="ozlistui-btn ozlistui-btn-delete" onClick={() => handleDelete(selectedReturn)}><FontAwesomeIcon icon={faTrash} /> {translations.delete || 'Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SolarReturnList;
